'use client'
import React from 'react'
import { useFormik } from 'formik'
import { signInEmailValidationSchema } from '@/src/validations/formvalidations'
import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import { GoogleSVG } from '@/src/ui/AllSVG'
import type { AuthView } from './LoginSignupIndex'
import { useUserResendOtpMutation } from '@/src/store/rtkQueries/userAuthApi'
import GoogleLogin from './GoogleLogin'
import type { VerifyEmailOtpSource } from './VerifyEmailOtpModal'

interface SignInEmailStepProps {
    navigate: (view: AuthView) => void
    onVerifyEmail: (email: string, source: VerifyEmailOtpSource) => void
}

export default function SignInEmailStep({ navigate, onVerifyEmail }: SignInEmailStepProps) {
    const [userResendOtp, { isLoading: isSubmitting }] = useUserResendOtpMutation()
    const { values, errors, touched, handleChange, handleBlur, handleSubmit, } = useFormik({
        initialValues: { email: '' },
        validationSchema: signInEmailValidationSchema,
        onSubmit: async (vals) => {
            try {
                const response = await userResendOtp({ email: vals.email })
                if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                    onVerifyEmail(vals.email, 'signin-email')
                    toast.success('OTP sent successfully')
                }
            } catch (error) {
                console.error('error', error);
            }
        },
    })

    return (
        <div className="flex flex-col gap-6 w-full">
            {/* Heading */}
            <div className="space-y-2 text-body-color">
                <h1 className="secondary_heading">
                    Sign In <span className="font-secondary italic">Now</span>
                </h1>
                <p className="text-sm">
                    By creating an account, I am also consenting to receive SMS, messages and emails.
                </p>
            </div>

            {/* Continue with Google */}
            <GoogleLogin />

            {/* Divider */}
            <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-muted-color" />
                <span className="text-body-color/40">or</span>
                <div className="flex-1 h-px bg-muted-color" />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div className="input_container">
                    <label className="form_label">Email address <span className="required_field">*</span></label>
                    <input
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="example@xyz.com"
                        className="form_input"
                    />
                    {errors.email && touched.email && (
                        <p className="error_text">{errors.email}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Button
                        type="button"
                        onClick={() => navigate('SignIn')}
                        className="global_btn rounded_full w-full outline_frosted"
                    >
                        Sign in with password
                    </Button>

                    <Button
                        type="submit"
                        className="global_btn rounded_full w-full text-sm font-medium tracking-wide"
                        isDisabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : 'Continue'}
                    </Button>
                </div>
            </form>

            {/* Switch to sign up */}
            <p className="text-sm text-center w-full text-body-color">
                Didn&apos;t have an account?{' '}
                <span onClick={() => navigate('SignUp')} className="accent_text">
                    Sign up
                </span>
            </p>
        </div>
    )
}
